import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { Fda510kModel } from '../../../../models/data-sections/fda/510kModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface Fda510kModalProps {
    searchExecutionId: string;
    selected_510k: Fda510kModel;
}

const Fda510kModal: React.FC<Fda510kModalProps> = ({
    searchExecutionId,
    selected_510k
}) => {
    const [k510Data, setK510Data] = useState<Fda510kModel | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [didErrorOccur, setDidErrorOccur] = useState(false);
    const { triggerSystemError } = useSystemError();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchApiGet510kDetails(searchExecutionId, selected_510k.k_number, new Date(selected_510k.__ci_system_search_date_key ?? 0));
            } catch (error) {
                console.error('Error during fetch:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [searchExecutionId, selected_510k]);

    const fetchApiGet510kDetails = async (search_execution_id: string, k_number: string, __ci_system_search_date_key: Date): Promise<void> => {
        try {
            const response = await fdaApi.get510kDetail(search_execution_id, k_number, __ci_system_search_date_key);

            if (response.success) {
                const data = (response as ResponseSuccessModel).data as any[];
                setK510Data(data?.[0] || null);
            } else {
                handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
            }
        } catch (error) {
            handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Typography variant="h6">
                    {k510Data?.device_name || '510(k) Application'}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                    {selected_510k.k_number} / {selected_510k.applicant}
                </Typography>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                    <CircularProgress />
                </Box>
            ) : didErrorOccur || !k510Data ? (
                <Typography color="error">Failed to load 510(k) details.</Typography>
            ) : (
                <Box mt={2}>
                    <Typography><strong>Device Name:</strong> {k510Data.device_name || 'N/A'}</Typography>
                    <Typography><strong>Applicant:</strong> {k510Data.applicant || 'N/A'}</Typography>
                    <Typography><strong>Clearance Type:</strong> {k510Data.clearance_type || 'N/A'}</Typography>
                    <Typography><strong>Review Panel:</strong> {k510Data.review_advisory_committee || 'N/A'}</Typography>
                    <Typography><strong>Decision Date:</strong> {k510Data.decision_date || 'N/A'}</Typography>

                    {k510Data.statement_or_summary && (
                        <Box mt={3}>
                            <Typography variant="h6">Summary</Typography>
                            <Typography paragraph>{k510Data.statement_or_summary}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default Fda510kModal;
